import type { DiffMode } from "../../library/gridState";
import css from "./primitives.module.css";

export const PIP_COUNT = 5;

/** §4: "gap 5-9px (scale with context)". */
const GAP_FOR = (size: number) => (size <= 11 ? 5 : size >= 20 ? 9 : 7);

type DisplayProps = {
  value: number;
  /** 10px in grid cards, 11px in recently-added/mobile detail, 14px in the detail rail. */
  size?: number;
  /** §4: "It appears on every card, row, and the detail rail as `n / 5`." */
  showRatio?: boolean;
};

/**
 * §4: five diamonds. "Never a dropdown, never a bare number."
 */
export function PipMeter({ value, size = 10, showRatio = false }: DisplayProps) {
  return (
    <span className={css.pips} style={{ gap: GAP_FOR(size) }}>
      {Array.from({ length: PIP_COUNT }, (_, i) => (
        <span
          key={i}
          aria-hidden
          className={`${css.pip} ${i < value ? css.pipFilled : css.pipEmpty}`}
          style={{ width: size, height: size }}
        />
      ))}
      {showRatio && <span className={css.pipDisplay}>{value} / 5</span>}
      <span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>
        difficulty {value} of {PIP_COUNT}
      </span>
    </span>
  );
}

/* ---- the interactive forms -----------------------------------------------

   There are two, and they were one until 2026-09-12 — DF-D3.

   `PipControl` did both jobs, and the filter's wording was baked into it, so
   Studio's difficulty picker read `3+ AND UP — CLEAR` for a tab that was simply
   rated 3. Nobody noticed because the component was right about the *mechanics*
   in both places and wrong only about what the number meant.

   Splitting them makes that impossible rather than unlikely: the two jobs no
   longer share a label, and every call site has to say which one it wants. */

type PipButtonsProps = {
  filled: (n: number) => boolean;
  title: (n: number) => string;
  onPick: (n: number) => void;
  size: number;
};

/** The five buttons. Which of them light up is the caller's business. */
function PipButtons({ filled, title, onPick, size }: PipButtonsProps) {
  return (
    <>
      {Array.from({ length: PIP_COUNT }, (_, i) => {
        const n = i + 1;
        const active = filled(n);
        return (
          <button
            key={n}
            type="button"
            title={title(n)}
            aria-pressed={active}
            className={`${css.pip} ${css.pipButton} ${active ? css.pipButtonActive : ""}`}
            style={{ width: size, height: size }}
            onClick={() => onPick(n)}
          />
        );
      })}
    </>
  );
}

type PickerProps = {
  value: number;
  onChange: (next: number) => void;
  /** 22px in the Studio form's picker. */
  size?: number;
};

/**
 * Setting a value — Studio's difficulty picker. FR-13.
 *
 * Fills `1..n`, which is the correct reading for a magnitude: a tab rated 3 has
 * three pips. Says nothing about direction, because there is no direction here —
 * this is the number itself, not a comparison against it.
 */
export function PipPicker({ value, onChange, size = 22 }: PickerProps) {
  return (
    <span className={css.pips} style={{ gap: GAP_FOR(size) }}>
      <PipButtons
        size={size}
        filled={(n) => n <= value}
        title={(n) => `set difficulty to ${n}`}
        // Clicking the pip that is already the value clears it back to unrated.
        onPick={(n) => onChange(value === n ? 0 : n)}
      />
      <button
        type="button"
        className={`${css.pipClear} ${value > 0 ? css.pipClearActive : ""}`}
        onClick={() => onChange(0)}
        disabled={value === 0}
      >
        {value === 0 ? "NOT SET" : `${value} / 5 — CLEAR`}
      </button>
    </span>
  );
}

type FilterProps = {
  value: number;
  mode: DiffMode;
  onChange: (next: number) => void;
  /** 20px in the filter row. */
  size?: number;
};

/**
 * The band that passes, under the current comparison — DF-D4.
 *
 * One rule, holding in all three modes: **the filled pips are exactly the
 * arrangements this filter will show.**
 *
 *     lte 3   1 2 3        eq 3   . . 3        gte 3   3 4 5
 *
 * Before the comparator existed, pips `1..n` filled while the filter meant "n
 * and up" — so the lit run showed the *opposite* of what passed, and read
 * correctly only because the label carried the meaning instead. That accident is
 * the reason this rule is written down rather than left to whoever edits next.
 */
export function PipFilter({ value, mode, onChange, size = 20 }: FilterProps) {
  const inBand = (n: number) => {
    if (value === 0) return false;
    if (mode === "lte") return n <= value;
    if (mode === "gte") return n >= value;
    return n === value;
  };

  const wording = (n: number) =>
    mode === "lte" ? `up to ${n}` : mode === "gte" ? `${n} and up` : `exactly ${n}`;

  return (
    <span className={css.pips} style={{ gap: GAP_FOR(size) }}>
      <PipButtons
        size={size}
        filled={inBand}
        title={(n) => `difficulty ${wording(n)}`}
        // Clicking the pip that is already the level clears the level. The mode
        // is deliberately untouched — FR-5.
        onPick={(n) => onChange(value === n ? 0 : n)}
      />
      <button
        type="button"
        className={`${css.pipClear} ${value > 0 ? css.pipClearActive : ""}`}
        onClick={() => onChange(0)}
        disabled={value === 0}
      >
        {value === 0 ? "ANY" : `${wording(value).toUpperCase()} — CLEAR`}
      </button>
    </span>
  );
}
